import { useTranslation } from "react-i18next";
import { Clock, Loader2 } from "lucide-react";

import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { openSupportChat } from "@/components/support/supportChatControls";
import type { TenantCurrentResponse } from "@/services/authService";

export interface OnboardingHomologacionPendingStepProps {
  tenant: TenantCurrentResponse | null;
}

/**
 * Parked "homologación pendiente" screen for self-scratch onboarding. The user
 * has done their safe self-serve part (created the DGI e-Factura user); the
 * Fiscalo team now completes the DGI homologación (SALKANTAI = Proveedor
 * Habilitado). `onboardingCompletedAt` stays null; the wizard keys off
 * `tenant.homologacionStatus` and auto-resumes (certificate → CAE) when the team
 * marks it completed/vigente. This screen just reassures + reflects status; the
 * parent (useOnboarding) polls the tenant so it advances on its own.
 */
export function OnboardingHomologacionPendingStep({
  tenant,
}: OnboardingHomologacionPendingStepProps): JSX.Element {
  const { t } = useTranslation();

  const companyName =
    tenant?.legalName?.trim() ||
    tenant?.name?.trim() ||
    t("onboarding.homologacionPending.companyFallback", "Tu empresa");

  return (
    <OnboardingShell
      form={
        <>
          <div className="fx-onb-ready-check" aria-hidden>
            <Clock />
          </div>
          <span className="fx-onb-step-tag">
            <Loader2
              className="animate-spin"
              aria-hidden
              style={{ width: 14, height: 14 }}
            />
            {t("onboarding.homologacionPending.eyebrow", "Homologación en curso")}
          </span>
          <h1 className="fx-onb-h1" style={{ marginTop: 12, marginBottom: 14 }}>
            <span data-mag-private>{companyName}</span>{" "}
            <span className="ital">
              {t(
                "onboarding.homologacionPending.h1Accent",
                "está por quedar habilitada.",
              )}
            </span>
          </h1>
          <p className="fx-onb-lead">
            {t(
              "onboarding.homologacionPending.lead",
              "Ya hiciste tu parte. Nuestro equipo está completando tu alta como emisor electrónico ante DGI — es un trámite que hacemos por vos y puede tardar unos días hábiles. Te avisamos por mail apenas puedas emitir; podés cerrar esta ventana y retomás desde acá (subir el certificado y los CAE).",
            )}
          </p>

          <div className="fx-onb-ready-summary">
            <div className="head">
              <span className="title">
                {t(
                  "onboarding.homologacionPending.summaryTitle",
                  "Qué está pasando",
                )}
              </span>
            </div>
            <div className="grid">
              <div className="cell">
                <span className="label">
                  {t("onboarding.homologacionPending.summaryStep", "Estado")}
                </span>
                <span className="value">
                  {t(
                    "onboarding.homologacionPending.summaryStepValue",
                    "Homologación con DGI",
                  )}
                </span>
                <span className="sub">
                  {t(
                    "onboarding.homologacionPending.summaryStepSub",
                    "Nuestro equipo lo está gestionando",
                  )}
                </span>
              </div>
              <div className="cell">
                <span className="label">
                  {t("onboarding.homologacionPending.summaryNext", "Cuando termine")}
                </span>
                <span className="value">
                  {t(
                    "onboarding.homologacionPending.summaryNextValue",
                    "Certificado + CAE",
                  )}
                </span>
                <span className="sub">
                  {t(
                    "onboarding.homologacionPending.summaryNextSub",
                    "Seguís desde acá, sin repetir nada",
                  )}
                </span>
              </div>
            </div>
          </div>
        </>
      }
      brand={
        <div className="fx-onb-brand-col fx-onb-ready-brand">
          <div>
            <span className="fx-onb-brand-eyebrow">
              <span className="dot" />
              {t(
                "onboarding.homologacionPending.brandEyebrow",
                "Lo hacemos por vos",
              )}
            </span>
            <p className="fx-onb-brand-quote">
              {t(
                "onboarding.homologacionPending.brandQuoteLead",
                "La homologación es la parte difícil.",
              )}{" "}
              <span className="accent">
                {t(
                  "onboarding.homologacionPending.brandQuoteAccent",
                  "La hacemos nosotros.",
                )}
              </span>
            </p>
          </div>
          <button
            type="button"
            className="fx-onb-ready-feedback"
            style={{ font: "inherit", textAlign: "left", cursor: "pointer" }}
            onClick={() => openSupportChat()}
          >
            <strong>
              {t(
                "onboarding.homologacionPending.feedbackLead",
                "¿Dudas mientras tanto?",
              )}
            </strong>
            <span>
              {t(
                "onboarding.homologacionPending.feedbackTail",
                "Escribinos — respondemos en menos de 1 hora.",
              )}
            </span>
          </button>
        </div>
      }
    />
  );
}
